import type { DataRow, ExcelField, FlagColor } from '@/types';

export type PersistedBatchTaskType = 'title' | 'filter' | 'size' | 'aiImage' | 'sku-spec' | 'sku-weight';
export type PersistedTaskStatus = 'running' | 'interrupted' | 'awaiting_confirmation' | 'completed' | 'failed';

export interface CollectionBoxTaskDraft {
    version: 1;
    taskId: string;
    sourceStatus: string;
    taskTypes: PersistedBatchTaskType[];
    selectedProductIds: string[];
    completedProductTasks: string[];
    status: PersistedTaskStatus;
    unsavedChanges: boolean;
    rows: DataRow[];
    originalRows: DataRow[];
    fields: ExcelField[];
    description: string;
    fileName: string;
    cellErrors: Record<number, Record<string, string>>;
    filteredRowIndices: number[];
    imageReplacements: Array<[string, string]>;
    productFlags: Record<string, FlagColor>;
    progress: { completed: number; total: number; message: string };
    createdAt: number;
    updatedAt: number;
    estimatedBytes: number;
}

const DB_NAME = 'tshopee-ai-collection-box';
const STORE_NAME = 'tasks';
const DB_VERSION = 1;
const MAX_BYTES = 200 * 1024 * 1024;
const DAY = 24 * 60 * 60 * 1000;
const ABANDONED_TASKS_KEY = 'tshopee-ai-collection-box-abandoned-tasks';

interface AbandonedTaskMarker {
    taskId: string;
    expiresAt: number;
}

function abandonedTaskMarkers(now = Date.now()): AbandonedTaskMarker[] {
    try {
        const parsed = JSON.parse(localStorage.getItem(ABANDONED_TASKS_KEY) || '[]');
        const markers = Array.isArray(parsed)
            ? parsed.filter(marker => marker && typeof marker.taskId === 'string' && Number(marker.expiresAt) > now)
            : [];
        localStorage.setItem(ABANDONED_TASKS_KEY, JSON.stringify(markers));
        return markers;
    } catch (_) {
        return [];
    }
}

export function markCollectionBoxTaskAbandoned(taskId: string): void {
    if (!taskId) return;
    try {
        const markers = abandonedTaskMarkers().filter(marker => marker.taskId !== taskId);
        markers.push({ taskId, expiresAt: Date.now() + 14 * DAY });
        localStorage.setItem(ABANDONED_TASKS_KEY, JSON.stringify(markers));
    } catch (_) { /* IndexedDB 删除仍会继续执行 */ }
}

export function isCollectionBoxTaskAbandoned(taskId: string): boolean {
    return abandonedTaskMarkers().some(marker => marker.taskId === taskId);
}

function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'taskId' });
                store.createIndex('updatedAt', 'updatedAt');
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB 打开失败'));
    });
}

function sanitize<T>(value: T): T {
    return JSON.parse(JSON.stringify(value, (_key, item) => {
        if (typeof item === 'string' && (/^data:image\//i.test(item) || (/^[A-Za-z0-9+/=]{4096,}$/.test(item) && !/^https?:\/\//i.test(item)))) {
            return '';
        }
        return item;
    })) as T;
}

function estimateBytes(value: unknown): number {
    return new Blob([JSON.stringify(value)]).size;
}

async function transaction<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await openDatabase();
    try {
        return await new Promise<T>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, mode);
            const request = action(tx.objectStore(STORE_NAME));
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error ?? new Error('IndexedDB 操作失败'));
        });
    } finally {
        db.close();
    }
}

export async function saveCollectionBoxTaskDraft(input: CollectionBoxTaskDraft): Promise<CollectionBoxTaskDraft> {
    const clean = sanitize(input);
    clean.estimatedBytes = estimateBytes({ ...clean, estimatedBytes: 0 });
    await transaction('readwrite', store => store.put(clean));
    return clean;
}

export async function deleteCollectionBoxTaskDraft(taskId: string): Promise<void> {
    await transaction('readwrite', store => store.delete(taskId));
}

export async function listCollectionBoxTaskDrafts(): Promise<CollectionBoxTaskDraft[]> {
    return await transaction('readonly', store => store.getAll()) as CollectionBoxTaskDraft[];
}

export async function loadRecoverableCollectionBoxTask(sourceStatus: string): Promise<CollectionBoxTaskDraft | null> {
    const tasks = await listCollectionBoxTaskDrafts();
    return tasks
        .filter(task => task.sourceStatus === sourceStatus && ['running', 'interrupted', 'awaiting_confirmation', 'failed'].includes(task.status))
        .sort((a, b) => b.updatedAt - a.updatedAt)[0] ?? null;
}

export async function cleanupCollectionBoxTaskDrafts(now = Date.now()): Promise<void> {
    abandonedTaskMarkers(now);
    const tasks = await listCollectionBoxTaskDrafts();
    const protectedTask = (task: CollectionBoxTaskDraft) => task.status === 'running' || task.status === 'awaiting_confirmation' || task.unsavedChanges;
    const expired = tasks.filter(task => {
        if (protectedTask(task)) return false;
        const retention = task.status === 'completed' ? 7 * DAY : 14 * DAY;
        return now - task.updatedAt > retention;
    });
    for (const task of expired) await deleteCollectionBoxTaskDraft(task.taskId);

    const remaining = tasks.filter(task => !expired.some(expiredTask => expiredTask.taskId === task.taskId));
    let totalBytes = remaining.reduce((sum, task) => sum + Number(task.estimatedBytes || 0), 0);
    const removable = remaining.filter(task => !protectedTask(task)).sort((a, b) => a.updatedAt - b.updatedAt);
    for (const task of removable) {
        if (totalBytes <= MAX_BYTES) break;
        await deleteCollectionBoxTaskDraft(task.taskId);
        totalBytes -= Number(task.estimatedBytes || 0);
    }
}

export function completedTaskKey(taskType: PersistedBatchTaskType, productId: string): string {
    return `${taskType}:${productId}`;
}
